import {create} from 'zustand';
import {CounselorGroupDTO, CounselorManagerDTO} from "../../../../types/counselor";
import {ValidStatus} from "../../../../types/common";
import {emailRegExp, pwdRegExp} from "../../../../util/regexpUtil";

interface CounselorState {
    groupDTO: CounselorGroupDTO;
    counselorDTO: CounselorManagerDTO;
    validates: {
        // group
        groupName:ValidStatus,
        address:ValidStatus,
        affiliationDistribution:ValidStatus,

        // counselor
        id: ValidStatus;
        cellPhone: ValidStatus;
        name: ValidStatus;
        password: ValidStatus;
        passwordConfirm: ValidStatus;
        affiliationIdx?: ValidStatus;
        consultantDistribution?: ValidStatus;
    };
    setCounselorDTO: (dto: CounselorManagerDTO) => void;
    setGroupDTO: (dto: CounselorGroupDTO) => void;
    setValidate: (key: keyof CounselorState['validates'], status: ValidStatus) => void;
    validateAll: () => boolean;
    validateGroup: () => boolean;
    validateCounselor: () => boolean;
}

export const useCounselorStore = create<CounselorState>((set, get) => ({
    groupDTO:{
        name: '',
        address: {country:'', city: '', address1:''},
        codePhone: '',
        phoneNumber: '',
        briefHistory: '',
        bankName: '',
        accountNumber: '',
        accountName: '',
        affiliationDistributionAmount: 0,
        consultantDistributionAmount: 0,
        isVatInclude: true,
        isTaxDeduction: true,
    },
    counselorDTO: {
        nationality: 'KR',
        email: '',
        name: '',
        codePhone: '+82',
        phoneNumber: '',
        password: '',
        passwordConfirm:'',
        briefHistory: '',
        bankName: '',
        accountNumber: '',
        accountName: '',
        addedImage: undefined,
        affiliationDistributionAmount: 0,
        consultantDistributionAmount: 0,
        isVatInclude: true,
        isTaxDeduction: true,
        counselingAffiliationIdx:''
    },
    validates: {
        // group
        groupName:ValidStatus.NONE,
        address:ValidStatus.NONE,
        affiliationDistribution:ValidStatus.NONE,

        // counselor
        id: ValidStatus.NONE,
        cellPhone: ValidStatus.NONE,
        name: ValidStatus.NONE,
        password: ValidStatus.NONE,
        passwordConfirm: ValidStatus.NONE,
        affiliationIdx: ValidStatus.NONE,
        consultantDistribution: ValidStatus.NONE,
    },
    setCounselorDTO: (dto) => set({ counselorDTO: dto }),
    setGroupDTO: (dto) => set({groupDTO: dto}),
    setValidate: (key, status) => set((state) => ({
        validates: {
            ...state.validates,
            [key]: status
        }
    })),
    validateAll: () => {
        const state = get();

        const groupValid = state.validateGroup();
        const counselorValid = state.validateCounselor();
        return groupValid && counselorValid;
    },

    validateGroup: () => {
        const { groupDTO, setValidate } = get();
        let isValid = true;

        if (!groupDTO) return true;

        if (groupDTO.name.length < 2) {
            setValidate('groupName', ValidStatus.INPUT_LENGTH);
            isValid = false;
        }

        if (groupDTO.address.address1 === '') {
            setValidate('address', ValidStatus.UNWRITTEN);
            isValid = false;
        }

        if (groupDTO.address.address2 === '') {
            setValidate('address', ValidStatus.DETAIL_UNWRITTEN);
            isValid = false;
        }

        if (groupDTO.affiliationDistributionAmount === 0 ||
            Number.isNaN(groupDTO.affiliationDistributionAmount)) {
            setValidate('affiliationDistribution', ValidStatus.UNWRITTEN);
            isValid = false;
        }

        if (groupDTO.consultantDistributionAmount === 0 ||
            Number.isNaN(groupDTO.consultantDistributionAmount)) {
            setValidate('consultantDistribution', ValidStatus.UNWRITTEN);
            isValid = false;
        }

        return isValid;
    },

    validateCounselor: () => {
        const { counselorDTO, validates, setValidate, groupDTO } = get();
        let isValid = true;

        if (!counselorDTO) return false;

        const isCounselorWithoutAffiliation = groupDTO.name ==="" && counselorDTO.counselingAffiliationIdx === "";
        const isCounselorIndependent = groupDTO.name ==="" && counselorDTO.counselingAffiliationIdx === "independent";

        if (counselorDTO.email.length < 6) {
            setValidate('id', ValidStatus.INPUT_LENGTH);
            isValid = false;
        }

        if (!emailRegExp.test(counselorDTO.email) && counselorDTO.email !== '') {
            setValidate('id', ValidStatus.WRONG_TYPE);
            isValid = false;
        }

        if (validates.id !== ValidStatus.VALID) {
            setValidate('id', ValidStatus.UNCHECKED);
            isValid = false;
        }

        if (counselorDTO.name.length < 2) {
            setValidate('name',ValidStatus.INPUT_LENGTH);
            isValid = false;
        }

        if (counselorDTO.phoneNumber.length < 11) {
            setValidate('cellPhone', ValidStatus.INPUT_LENGTH);
            isValid = false;
        }

        if (validates.cellPhone !== ValidStatus.VALID) {
            setValidate('cellPhone', ValidStatus.UNCHECKED);
            isValid = false;
        }

        if (!pwdRegExp.test(counselorDTO.password)) {
            setValidate('password',ValidStatus.WRONG_TYPE);
            isValid = false;
        }

        if (counselorDTO.password !== counselorDTO.passwordConfirm) {
            setValidate('passwordConfirm',ValidStatus.MISMATCH);
            isValid = false;
        }

        // 소속을 선택 안한 경우
        if (isCounselorWithoutAffiliation) {
            setValidate('affiliationIdx',ValidStatus.UNWRITTEN)
            isValid = false;
        }

        if (isCounselorWithoutAffiliation && counselorDTO.consultantDistributionAmount === 0) {
            setValidate('consultantDistribution',ValidStatus.UNWRITTEN)
            isValid = false;
        }

        // 무소속인데 분배금을 입력 안한 경우
        if (isCounselorIndependent && counselorDTO.consultantDistributionAmount === 0) {
            setValidate('consultantDistribution', ValidStatus.UNWRITTEN)
            isValid = false;
        }

        return isValid;
    }
}));